// @ts-strict-ignore
import { useEffect, useState } from "react";

import { ModularStoreSetupSteps } from "@shared/types/ModularStoreSetupSteps";
import type { NextStep, StepDetails, TotalStepCount } from "@shared/types/graphqlGenerated";

const useStepDetails = (steps: StepDetails[]) => {
  const firstIncompleteStepIndex = steps?.findIndex(step => !step.stepCompleted);
  const [currentStepIndex, setCurrentStepIndex] = useState(firstIncompleteStepIndex);

  // A stack to keep track of what steps the user should go through again if they
  // go back multiple steps.
  const [backStepStack, setBackStepStack] = useState([]);

  // reset current step when data is updated after a mutation
  useEffect(() => {
    setBackStepStack([]);
    setCurrentStepIndex(firstIncompleteStepIndex);
  }, [firstIncompleteStepIndex]);

  if (!steps) return {};

  const isCompletedStep = currentStepIndex === -1;

  const getNextIncompleteStep = (stepsList: StepDetails[]) => {
    const remainingSteps = stepsList.slice(currentStepIndex + 1);
    const incompleteSteps = remainingSteps?.filter(step => !step.stepCompleted);
    const nextStep = incompleteSteps?.[0];

    return nextStep ? nextStep.name : ModularStoreSetupSteps.COMPLETE;
  };

  const getNextStep = (): NextStep => {
    if (isCompletedStep) {
      return {
        variableMargin: null,
        neverMargin: null,
      };
    }

    return {
      variableMargin: getNextIncompleteStep(steps),
      neverMargin: getNextIncompleteStep(steps),
    };
  };

  const prevStepIndex = currentStepIndex > 0 ? currentStepIndex - 1 : null;

  const goBack = () => {
    setBackStepStack([...backStepStack, steps[currentStepIndex]]);
    setCurrentStepIndex(currentStepIndex > 0 ? prevStepIndex : 0);
  };

  const goNext = () => {
    if (backStepStack.length > 0) {
      const overrideCurrentStep = backStepStack.pop();
      const overrideCurrentStepIndex = steps.findIndex(
        step => step.name === overrideCurrentStep.name
      );
      setCurrentStepIndex(overrideCurrentStepIndex);
    }
  };

  const totalStepCount: TotalStepCount = {
    neverMargin: steps.length,
    variableMargin: steps.length,
  };

  const currentStep = isCompletedStep
    ? ModularStoreSetupSteps.COMPLETE
    : steps[currentStepIndex]?.name;

  return {
    currentStep,
    currentStepNumber: !isCompletedStep ? currentStepIndex + 1 : currentStepIndex,
    nextStep: getNextStep(),
    goBack,
    goNext,
    previousStep: prevStepIndex !== null ? steps[prevStepIndex].name : null,
    totalStepCount,
  };
};

export { useStepDetails };
